import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FormattingToolbar from "../Composer/FormattingToolbar";
import FooterActions from "../Composer/FooterActions";
import RichTextInput from "../Composer/RichTextInput";
import MentionDropdown from "../Composer/MentionDropdown";
import { COMPOSER_FORMAT_TOGGLE_STORAGE_KEY } from "../Composer/constants";
import { computeCanSend, loadToggle, persistToggle } from "../Composer/composerUtils";
import useComposerMentions from "../../../hooks/useComposerMentions";
import useComposerFormatting from "../../../hooks/useComposerFormatting";
import { extractImageFilesFromClipboardEvent } from "../../../utils/clipboard";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { MOBILE_MAX_WIDTH, mediaQueryMaxWidth } from "../../../constants/breakpoints";

const ChatComposer = forwardRef(function ChatComposer({
  valueHtml,
  onChangeHtml,
  isSending,
  onSend,
  mentionUsers,
  mentionChannels,
  onInsertCanvas,
  ariaLabel,
  placeholder,
  placeholderTarget,
  activePlaceholder,
  onSelectEmoji,
  accessory,
}, externalEditorRef) {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const composerRef = useRef(null);
  const [showFormatBar, setShowFormatBar] = useState(() => loadToggle(COMPOSER_FORMAT_TOGGLE_STORAGE_KEY, true));
  const [isEditorFocused, setIsEditorFocused] = useState(false);

  const resolvedInactivePlaceholder = (() => {
    if (typeof placeholder === "string" && placeholder.trim()) {
      return placeholder;
    }
    if (typeof placeholderTarget === "string" && placeholderTarget.trim()) {
      return t("composerV2.placeholderInactive", { target: placeholderTarget });
    }
    return t("composerV2.placeholderFallback");
  })();
  const resolvedActivePlaceholder = (typeof activePlaceholder === "string" && activePlaceholder.trim())
    ? activePlaceholder
    : t("composerV2.placeholderActive");
  // The active placeholder explains Enter/Shift+Enter, which only applies to
  // hardware keyboards — keep the "Message {target}" prompt on mobile.
  const isMobileViewport = useMediaQuery(mediaQueryMaxWidth(MOBILE_MAX_WIDTH));
  const editorPlaceholder = (isEditorFocused && !isMobileViewport)
    ? resolvedActivePlaceholder
    : resolvedInactivePlaceholder;

  useEffect(() => { persistToggle(COMPOSER_FORMAT_TOGGLE_STORAGE_KEY, showFormatBar); }, [showFormatBar]);

  // Track object URLs created for inline image previews so we can revoke them on unmount
  const blobUrlsRef = useRef(new Set());
  const pendingFilesRef = useRef(new Map()); // Map<blobUrl, File>
  useEffect(() => () => {
    try {
      blobUrlsRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch (_) {} });
      blobUrlsRef.current.clear();
      pendingFilesRef.current.clear();
    } catch (_) {}
  }, []);

  const ingestImageFiles = useCallback((files) => {
    if (!files) return;
    const list = Array.from(files).filter((file) => {
      if (!file) return false;
      if (!file.type) return true; // allow when clipboard omits type; exec guard later
      return file.type.startsWith("image/");
    });
    if (!list.length) {
      return;
    }
    list.forEach((file) => {
      try {
        const url = URL.createObjectURL(file);
        blobUrlsRef.current.add(url);
        try {
          pendingFilesRef.current.set(url, file);
        } catch (_) { /* ignore map errors */ }
        const safeName = (file?.name || (file?.type ? file.type.replace(/\W+/g, "-") : "image")).replace(/["<>]/g, "").slice(0, 200);
        const html = `<img src="${url}" alt="${safeName}" loading="lazy">`;
        editorRef.current?.exec?.("html", `${html}<br>`);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to ingest pasted image", error);
      }
    });
  }, []);

  const handleSelectImages = useCallback((files) => {
    if (!Array.isArray(files) || !files.length) return;
    ingestImageFiles(files);
  }, [ingestImageFiles]);

  const handleEditorPaste = useCallback((event) => {
    const fileItems = extractImageFilesFromClipboardEvent(event);
    if (fileItems.length) {
      event.preventDefault();
      ingestImageFiles(fileItems);
      const pastedText = typeof event.clipboardData?.getData === "function"
        ? event.clipboardData.getData("text/plain")
        : "";
      if (pastedText && pastedText.trim()) {
        try {
          editorRef.current?.exec?.("text", `${pastedText}\n`);
        } catch (_) {
          // ignore text injection failures
        }
      }
    }
  }, [ingestImageFiles]);

  const {
    mentionState,
    mentionActiveIndex,
    setMentionActiveIndex,
    refreshMentionState,
    closeMentionDropdown,
    commitMention,
    handleMentionSelect,
    handleInsertMention,
  } = useComposerMentions({ editorRef, composerRef, userOptions: mentionUsers || [], channelOptions: mentionChannels || [] });

  const { formatState, recomputeFormatState, applyWrapFormatting, applyCodeFormatting, applyQuoteFormatting, applyListFormatting, applyLinkFormatting } = useComposerFormatting(editorRef);

  const emitSendIfAllowed = useCallback((draftHtml) => {
    const canSend = computeCanSend({ editorRef, draftHtml, isSending });
    if (!canSend) return false;
    closeMentionDropdown();
    // Pass pending files map so parent can upload/replace blob URLs
    let pendingFiles = [];
    try { pendingFiles = Array.from(pendingFilesRef.current.entries()); } catch (_) { pendingFiles = []; }
    const result = onSend?.({ html: draftHtml, pendingFiles });
    const clearPendingEntries = (entries) => {
      if (!Array.isArray(entries) || !entries.length) return;
      entries.forEach(([blobUrl, file]) => {
        try {
          const currentFile = pendingFilesRef.current.get(blobUrl);
          if (currentFile === file) {
            pendingFilesRef.current.delete(blobUrl);
            if (blobUrlsRef.current.has(blobUrl)) {
              try { URL.revokeObjectURL(blobUrl); } catch (_) {}
              blobUrlsRef.current.delete(blobUrl);
            }
          }
        } catch (_) {
          // Ignore map mutation errors; we'll clean up on unmount.
        }
      });
    };
    if (result && typeof result.then === "function") {
      result
        .then((value) => {
          if (value !== false) {
            clearPendingEntries(pendingFiles);
          }
        })
        .catch(() => {
          // keep pending files for retry on failure
        });
      return true;
    }
    if (result !== false) {
      clearPendingEntries(pendingFiles);
      return true;
    }
    return false;
  }, [closeMentionDropdown, editorRef, isSending, onSend]);

  const handleKeyDown = useCallback((event) => {
    if (mentionState.isOpen) {
      if (event.key === "ArrowDown") { event.preventDefault(); if (mentionState.items.length) setMentionActiveIndex((i) => (i + 1) % mentionState.items.length); return; }
      if (event.key === "ArrowUp") { event.preventDefault(); if (mentionState.items.length) setMentionActiveIndex((i) => (i - 1 + mentionState.items.length) % mentionState.items.length); return; }
      if (event.key === "Enter" || event.key === "Tab") { event.preventDefault(); handleMentionSelect(mentionActiveIndex); return; }
      if (event.key === "Escape") { event.preventDefault(); closeMentionDropdown(); return; }
    }
    if (event.isComposing) return;
    const isEnter = event.key === "Enter" || event.key === "NumpadEnter";
    if (isEnter && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      let currentHtml = editorRef.current?.getHtml?.() ?? valueHtml;
      if (currentHtml !== valueHtml) onChangeHtml?.(currentHtml);
      if (!emitSendIfAllowed(currentHtml)) {
        const el = editorRef.current?.getElement?.();
        const liveText = ((el?.textContent || '').replace(/\u00A0/g, ' ').trim());
        if (liveText.length > 0) {
          currentHtml = liveText.replace(/\n/g, '<br>');
          onChangeHtml?.(currentHtml);
          emitSendIfAllowed(currentHtml);
        }
      }
    }
  }, [emitSendIfAllowed, mentionActiveIndex, mentionState.items.length, mentionState.isOpen, onChangeHtml, valueHtml, closeMentionDropdown, handleMentionSelect, setMentionActiveIndex]);

  const handleClickSend = useCallback(() => {
    let currentHtml = editorRef.current?.getHtml?.() ?? valueHtml;
    if (currentHtml !== valueHtml) onChangeHtml?.(currentHtml);
    if (!emitSendIfAllowed(currentHtml)) {
      const el = editorRef.current?.getElement?.();
      const liveText = ((el?.textContent || '').replace(/\u00A0/g, ' ').trim());
      if (liveText.length > 0) {
        currentHtml = liveText.replace(/\n/g, '<br>');
        onChangeHtml?.(currentHtml);
        emitSendIfAllowed(currentHtml);
      }
    }
  }, [emitSendIfAllowed, onChangeHtml, valueHtml]);

  useEffect(() => {
    const handler = () => {
      try { refreshMentionState(); recomputeFormatState(); } catch (_) {}
    };
    document.addEventListener('selectionchange', handler);
    return () => { document.removeEventListener('selectionchange', handler); };
  }, [recomputeFormatState, refreshMentionState]);

  React.useImperativeHandle(externalEditorRef, () => ({
    getHtml: () => editorRef.current?.getHtml?.(),
    getElement: () => editorRef.current?.getElement?.(),
    focus: () => editorRef.current?.focus?.(),
    exec: (...args) => editorRef.current?.exec?.(...args),
  }));

  return (
    <div
      ref={composerRef}
      className={`composer-v2${isEditorFocused ? " composer-v2--focused" : ""}`}
      role="form"
      aria-label={ariaLabel}
      aria-busy={isSending}
    >
      {accessory}

      {showFormatBar && (
        <FormattingToolbar
          onBold={() => applyWrapFormatting({ kind: 'bold' })}
          onItalic={() => applyWrapFormatting({ kind: 'italic' })}
          onStrike={() => applyWrapFormatting({ kind: 'strike' })}
          onLink={() => applyLinkFormatting()}
          onOrdered={() => applyListFormatting({ ordered: true })}
          onBulleted={() => applyListFormatting({ ordered: false })}
          onQuote={() => applyQuoteFormatting()}
          onCode={() => applyCodeFormatting()}
          isBold={formatState.bold}
          isItalic={formatState.italic}
          isStrike={formatState.strike}
          isLink={formatState.link}
          isOrderedList={formatState.ordered}
          isBulletedList={formatState.bulleted}
          isQuote={formatState.quote}
          isCode={formatState.code}
        />
      )}

      <RichTextInput
        ref={editorRef}
        className="composer-v2__field"
        ariaLabel={ariaLabel}
        valueHtml={valueHtml}
        onChangeHtml={(html) => { onChangeHtml?.(html); refreshMentionState(); recomputeFormatState(); }}
        placeholder={editorPlaceholder}
        onKeyDown={handleKeyDown}
        onPaste={handleEditorPaste}
        onFocus={() => {
          setIsEditorFocused(true);
          refreshMentionState();
        }}
        onClick={() => refreshMentionState()}
        onBlur={() => {
          setIsEditorFocused(false);
          // Mention dropdown will be closed by footer or selection handlers.
        }}
        disabled={isSending}
      />

      <FooterActions
        onToggleFormatting={() => setShowFormatBar((v) => !v)}
        formattingActive={showFormatBar}
        onInsertMention={handleInsertMention}
        onStartVideo={() => {}}
        onRecordAudio={() => {}}
        onCreateCanvas={onInsertCanvas}
        onSend={handleClickSend}
        canSend={computeCanSend({ editorRef, draftHtml: valueHtml, isSending })}
        isSending={isSending}
        onSelectEmoji={(emoji) => {
          if (onSelectEmoji) onSelectEmoji(emoji);
          try { editorRef.current?.exec('text', emoji?.native || ''); } catch (_) {}
        }}
        onSelectImages={handleSelectImages}
      />
      {mentionState.isOpen && (
        <MentionDropdown
          anchor={mentionState.anchor}
          items={mentionState.items}
          activeIndex={mentionActiveIndex}
          type={mentionState.type}
          query={mentionState.query}
          onSelect={(item) => {
            const before = editorRef.current?.getHtml?.() || "";
            commitMention(item);
            const after = editorRef.current?.getHtml?.();
            if (typeof after === 'string' && after !== before) {
              onChangeHtml?.(after);
            }
          }}
          onHighlight={setMentionActiveIndex}
        />
      )}
    </div>
  );
});

ChatComposer.propTypes = {
  valueHtml: PropTypes.string.isRequired,
  onChangeHtml: PropTypes.func.isRequired,
  isSending: PropTypes.bool,
  onSend: PropTypes.func.isRequired,
  mentionUsers: PropTypes.array,
  mentionChannels: PropTypes.array,
  onInsertCanvas: PropTypes.func,
  ariaLabel: PropTypes.string,
  placeholder: PropTypes.string,
  placeholderTarget: PropTypes.string,
  activePlaceholder: PropTypes.string,
  onSelectEmoji: PropTypes.func,
  accessory: PropTypes.node,
};

ChatComposer.defaultProps = {
  isSending: false,
  mentionUsers: undefined,
  mentionChannels: undefined,
  onInsertCanvas: undefined,
  ariaLabel: "Message composer",
  placeholder: "",
  placeholderTarget: "",
  activePlaceholder: "",
  onSelectEmoji: undefined,
  accessory: null,
};

export default ChatComposer;
